import { useState } from 'react';
import { Stack, Title, SimpleGrid, Paper, Group, Tooltip, ActionIcon, SegmentedControl } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import {
  TasteProfileData,
  SlotTasteProfiles,
  TECHNIQUE_KEYS,
  TECHNIQUE_LABELS,
  FORMAT_KEYS,
  FORMAT_LABELS,
  FLAVOR_KEYS,
  FLAVOR_LABELS,
  TEXTURE_KEYS,
  TEXTURE_LABELS,
} from '../../types/tasteProfile';
import { TasteRadarChart } from './TasteRadarChart';
import { CuisineHeatStrip } from './CuisineHeatStrip';
import { IngredientGrid } from './IngredientGrid';
import { PreferencePillStrip } from './PreferencePillStrip';

type TasteProfileSectionProps = {
  tasteProfile: TasteProfileData;
  // Breakfast/lunch learning, present only once a slot passes the backend's
  // chart floor. Absent (every user pre-slots, most users early on) renders
  // the section exactly as it always has: dinner data, no meal control.
  slotTasteProfiles?: SlotTasteProfiles;
  ownerName: string;
};

type TasteSlotTab = 'breakfast' | 'lunch' | 'dinner';

export const TasteProfileSection = ({ tasteProfile, slotTasteProfiles, ownerName }: TasteProfileSectionProps) => {
  // Dinner is the default: it is the richest state and the pre-slots story
  const [activeSlot, setActiveSlot] = useState<TasteSlotTab>('dinner');
  const slotOptions = [
    ...(slotTasteProfiles?.breakfast ? [{ value: 'breakfast', label: 'Breakfast' }] : []),
    ...(slotTasteProfiles?.lunch ? [{ value: 'lunch', label: 'Lunch' }] : []),
    { value: 'dinner', label: 'Dinner' },
  ];
  const active =
    (activeSlot === 'breakfast' && slotTasteProfiles?.breakfast) ||
    (activeSlot === 'lunch' && slotTasteProfiles?.lunch) ||
    tasteProfile;
  return (
    <Stack gap="md">
      <Group gap="xs" align="center" justify="space-between">
        <Group gap="xs" align="center">
        <Title order={3}>Taste profile</Title>
        <Tooltip
          label={`A snapshot of ${ownerName}'s food personality, shaped by their cooking history and preferences over time.`}
          multiline
          w={260}
          withArrow
          events={{ hover: true, focus: true, touch: true }}
        >
          <ActionIcon variant="subtle" color="gray" size="sm">
            <IconInfoCircle size={18} />
          </ActionIcon>
        </Tooltip>
        </Group>
        {slotOptions.length > 1 && (
          <SegmentedControl
            value={activeSlot}
            onChange={(value) => setActiveSlot(value as TasteSlotTab)}
            data={slotOptions}
            size="xs"
            radius="xl"
            color="orange"
          />
        )}
      </Group>

      <Paper p="lg" radius="lg" withBorder>
        <Stack gap="lg">
          {/* The two radars sit together: same shape, same scale, so flavor
              and mouthfeel read as one comparison rather than two widgets.
              Each hides itself when the user has no data on that axis. */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <TasteRadarChart
              title="Flavor profile"
              axisKeys={FLAVOR_KEYS}
              axisLabels={FLAVOR_LABELS}
              weights={active.flavorWeights}
            />
            <TasteRadarChart
              title="Texture profile"
              axisKeys={TEXTURE_KEYS}
              axisLabels={TEXTURE_LABELS}
              weights={active.textureWeights ?? {}}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <CuisineHeatStrip cuisineWeights={active.cuisineWeights} summary={active.summary} />
            <PreferencePillStrip
              title="Technique preferences"
              keys={TECHNIQUE_KEYS}
              labels={TECHNIQUE_LABELS}
              weights={active.techniqueWeights}
              preferredKeys={active.summary.preferredTechniques}
              avoidedKeys={active.summary.avoidedTechniques}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <PreferencePillStrip
              title="Format preferences"
              keys={FORMAT_KEYS}
              labels={FORMAT_LABELS}
              weights={active.formatWeights}
              preferredKeys={active.summary.preferredFormats}
              avoidedKeys={active.summary.avoidedFormats}
            />
          </SimpleGrid>
          <IngredientGrid
            ingredientWeights={active.ingredientWeights}
            summary={active.summary}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};
